import { EventDateType, SearchEventType } from "globals/types/models.ts";
import { useAppDispatch, useAppSelector } from "config/store";
import { adminActions } from "config/store/modules/admin.ts";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminConnectedContainer from "components/container/AdminConnectedContainer.tsx";
import dayjs from "dayjs";
import Button from "components/atom/button/Button.tsx";
import Icon from "components/atom/Icon.tsx";
import { getClosestNextDate } from "utils/data/event.ts";
import { LANGS } from "globals/constants.ts";
import { useTranslation } from "react-i18next";
import Input from "components/atom/Input.tsx";

// Component for sortable column headers
interface SortableHeaderProps {
  field: string;
  currentSortField: string;
  sortDirection: "ASC" | "DESC";
  onSort: (field: string) => void;
  children: React.ReactNode;
  className?: string;
}

function SortableHeader(props: SortableHeaderProps) {
  const {
    field,
    currentSortField,
    sortDirection,
    onSort,
    children,
    className = ""
  } = props;
  const isActive = currentSortField === field;

  return (
    <div
      className={`flex items-center cursor-pointer ${className}`}
      onClick={() => onSort(field)}
    >
      <p className="text-sm font-medium text-black select-none">
        {children}
      </p>
      <div className="ml-2 flex flex-row">
        <Icon
          name="chevron-down"
          className={`w-4 h-4 ${isActive && sortDirection === "ASC" ? "text-green-500" : "text-gray-400"}`}
        />
        <Icon
          name="chevron-down"
          className={`w-4 h-4  transform rotate-180 ${isActive && sortDirection === "DESC" ? "text-green-500" : "text-gray-400"}`}
        />
      </div>
    </div>
  );
}


function AdminEventsPage() {

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const currentLang = useAppSelector(state => state.global.currentLang);
  const events: SearchEventType[] = useAppSelector(state => state.admin.events);
  const pagination = useAppSelector(state => state.admin.eventsPagination);
  const sorting = useAppSelector(state => state.admin.eventsSorting);

  const [searchValue, setSearchValue] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [sortField, setSortField] = useState<string>(sorting?.field || "id");
  const [sortDirection, setSortDirection] = useState<"ASC" | "DESC">(sorting?.direction || "DESC");

  const searchEvents = useCallback(() => {
    dispatch(adminActions.getEvents(searchValue, currentPage, 100, sortField, sortDirection));
  }, [dispatch, searchValue, currentPage, sortField, sortDirection]);

  //init by searching all events on mount
  //search event when it's empty or when page changes
  useEffect(() => {
    searchEvents();
  }, [searchEvents, currentPage]);

  const goToAdminEvent = useCallback((eventUuid: string) => {
    return () => {
      navigate(`/profile/admin/events/${eventUuid}`);
    };
  }, [navigate]);

  const goToPublicEvent = useCallback((eventHandle: string) => {
    return () => {
      window.open(`/events/${eventHandle}`, "_blank");
    };
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handleSort = useCallback((field: string) => {
    setCurrentPage(1);
    //for id just switch
    if (field === "id") {
      setSortField("id");
      setSortDirection(prev => prev === "ASC" ? "DESC" : "ASC");
      return;
    }

    // If clicking the same field, toggle direction
    if (field === sortField) {
      if (sortDirection === "ASC") {
        setSortDirection("DESC");
        return;
      }

      if (sortDirection === "DESC") {
        //reset
        setSortField("id");
        setSortDirection("DESC");//init with ASC
        return;
      }
    }


    // If clicking a new field, set it and default to DESC
    setSortField(field);
    setSortDirection("ASC");//init with ASC


  }, [sortField, sortDirection]);

  // Generate page numbers with ellipsis for pagination
  const pageNumbers = useMemo<(string | number)[]>(() => {
    if (!pagination) {
      return [];
    }
    const delta = 2; // Number of pages to show before and after current page
    const pages: (number | string)[] = [];

    // Always add first page
    pages.push(1);

    // Calculate range of pages to show around current page
    const leftBound = Math.max(2, currentPage - delta);
    const rightBound = Math.min(pagination.totalPages - 1, currentPage + delta);

    // Add ellipsis after first page if needed
    if (leftBound > 2) {
      pages.push("...");
    }

    // Add pages around current page
    for (let i = leftBound; i <= rightBound; i++) {
      pages.push(i);
    }

    // Add ellipsis before last page if needed
    if (rightBound < pagination.totalPages - 1) {
      pages.push("...");
    }

    // Always add last page if not already included
    if (pagination.totalPages > 1) {
      pages.push(pagination.totalPages);
    }

    return pages;
  }, [currentPage, pagination]);

  return (
    <AdminConnectedContainer
      title={t("menu.admin.events")}
      rightTitle={(
        <Fragment>
          <Button small onClick={() => navigate("/profile/admin/events/create")}>
            {t("event.create")}
          </Button>
        </Fragment>
      )}>
      <div className="w-full space-y-2">
        <div className="flex flex-row justify-center items-center gap-4 w-full"
             onKeyUp={(e) => {
               if (e.key === "Enter") {
                 searchEvents();
               }
             }}
        >
          <Input name="search"
                 label="Recherche"
                 className="flex-1"
                 formikField={false}
                 value={searchValue}
                 onChange={(e) => {
                   const newValue = e.target.value;
                   setSearchValue(newValue);
                 }}
          />

          <button className="mt-5 w-12 h-10 bg-black rounded-md flex flex-row justify-center items-center"
                  type="button" onClick={searchEvents}>
            <Icon name="search" className="min-w-4 w-4 h-4 block text-white" />
          </button>
        </div>
        <div className="flex flex-row items-center justify-start gap-4 border-b border-gray-300">
          <div className="w-1/12">
            <SortableHeader
              field="id"
              currentSortField={sortField}
              sortDirection={sortDirection}
              onSort={handleSort}
            >
              ID
            </SortableHeader>
          </div>
          <div className="w-3/12">
            <SortableHeader
              field="titleFr"
              currentSortField={sortField}
              sortDirection={sortDirection}
              onSort={handleSort}
            >
              {t("forms.title")}
            </SortableHeader>
          </div>
          <div className="w-2/12">
            <SortableHeader
              field="address"
              currentSortField={sortField}
              sortDirection={sortDirection}
              onSort={handleSort}
            >
              {t("forms.streetAddress")}
            </SortableHeader>
          </div>
          <div className="w-2/12">
            <SortableHeader
              field="startDate"
              currentSortField={sortField}
              sortDirection={sortDirection}
              onSort={handleSort}
            >
              {t("forms.startDate")}
            </SortableHeader>
          </div>
          <div className="w-2/12">
            <SortableHeader
              field="creatorEmail"
              currentSortField={sortField}
              sortDirection={sortDirection}
              onSort={handleSort}
            >
              {t("event.creator")}
            </SortableHeader>
          </div>
          <div className="w-1/12">
            <SortableHeader
              field="viewCount"
              currentSortField={sortField}
              sortDirection={sortDirection}
              onSort={handleSort}
            >
              {t("event.viewCount")}
            </SortableHeader>
          </div>

          <div className="w-1/12" />
        </div>
        {events.map(event => {
          const eventTitle = currentLang === LANGS.FR ? event.titleFr : event.titleEn ? event.titleEn : event.titleFr;
          let closestDate = getClosestNextDate<EventDateType>(event.dates);
          //for admin, just show the first date
          if (!closestDate && event.dates.length > 0) {
            closestDate = event.dates[0];
          }
          return (
            <div
              className={`flex flex-row items-center justify-start gap-4 ${event.deletedAt ? "bg-red-100" : "bg-gray-50"}`}
              key={event.id}

            >
              <div className="w-1/12 flex flex-row justify-start items-center gap-2">
                <p className="text-black pl-1 text-sm">
                  #{event.id}
                </p>
                {event.signaledCount > 0 && (
                  <Icon name="alert-circle" className="block w-4 h-4 text-red-500" />
                )}
                {event.externalSource === "facebook" && (
                  <img src="/icons/facebook.png" className="block w-4 h-4" />
                )}
                {event.externalSource === "mtl-org" && (
                  <img src="/icons/mtl-org.png" className="block w-4 h-4" />
                )}
                {event.externalSource === "blog-toronto" && (
                  <img src="/icons/blog-toronto.png" className="block w-4 h-4" />
                )}
                {event.externalSource === "choose-chicago" && (
                  <img src="/icons/choose-chicago.png" className="block w-4 h-4" />
                )}
                {event.externalSource === "miami-beach" && (
                  <img src="/icons/miami-beach.png" className="block w-4 h-4" />
                )}
                {event.externalSource === "discover-los-angeles" && (
                  <img src="/icons/discover-los-angeles.png" className="block w-4 h-4" />
                )}
              </div>
              <div className="w-3/12">
                <p className="text-black text-sm">
                  {eventTitle}
                </p>
              </div>
              <div className="w-2/12">
                <p className="text-black text-sm">
                  {event.address}
                </p>
              </div>
              <div className="w-2/12">
                <p className="text-black text-sm">
                  {closestDate ? dayjs(closestDate.startDate).format("D MMM YYYY HH:mm") : ""}
                </p>
              </div>
              <div className="w-2/12">
                {event.creator ? (
                  <a href={`/profile/admin/users/${event.creator.id}`}
                     className="text-black text-sm underline cursor-pointer">
                    {event.creator.email}
                  </a>
                ) : (
                  <span className="text-sm">Aucun</span>
                )}

              </div>
              <div className="w-1/12">
                {event.eventStatCount && (
                  <p className="text-sm text-black">
                    {event.eventStatCount.viewCount}
                  </p>
                )}
              </div>

              <div className="w-1/12 flex flex-row justify-start items-center gap-4">
                <button type="button" className="text-sm underline" onClick={goToAdminEvent(event.uuid)}>
                  {t("edit")}
                </button>
                <button type="button" className="text-sm underline mr-1"
                        onClick={goToPublicEvent(event.handle)}>
                  {t("see")}
                </button>
              </div>
            </div>
          );
        })}

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="flex flex-row justify-center items-center">
            <div className="flex flex-row justify-start items-center gap-2 mt-6">
              <Button
                small
                style="border"
                onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
              >
                {t("pagination.previous")}
              </Button>

              {pageNumbers.map((page, index) =>
                page === "..." ? (
                  <span key={`ellipsis-${index}`} className="px-2 text-center">
                    ...
                  </span>
                ) : (
                  <Button
                    className="min-w-10"
                    small
                    style={currentPage === page ? "normal" : "border"}
                    key={`page-${page}`}
                    onClick={() => handlePageChange(page as number)}
                  >
                    {page}
                  </Button>
                )
              )}

              <Button
                small
                style="border"
                onClick={() => handlePageChange(Math.min(pagination.totalPages, currentPage + 1))}
                disabled={currentPage === pagination.totalPages}
              >
                {t("pagination.next")}
              </Button>
            </div>
          </div>
        )}

        {/* Pagination info */}
        {pagination && pagination.totalPages > 0 && (
          <div className="text-center text-sm text-gray-500 mt-2">
            {t("pagination.page")} {pagination.page} {t("pagination.of")} {pagination.totalPages}
          </div>
        )}
      </div>
    </AdminConnectedContainer>
  );
}

export default AdminEventsPage;
